import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["readOnly", "required", "extraOptions", "defaultOptions", "type"];
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { debounce, isEqual } from 'underscore';
import UITruncateString from 'UIComponents/text/UITruncateString';
import Small from 'UIComponents/elements/Small';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import AsyncSelector from 'ContentComponents/cmv2/components/AsyncSelector';
import * as PagesActions from 'ContentData/actions/Pages';
import * as pagesSelectors from 'ContentData/selectors/pages';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/cmv2/constants';
import { PAGE } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function mapStateToProps(state) {
  return {
    options: pagesSelectors.getPageOptions(state),
    pages: pagesSelectors.getPages(state),
    defaultOptions: pagesSelectors.getDefaultPageOptions(state),
    optionsRequestStatus: pagesSelectors.getPagesFetchStatus(state)
  };
}
const mapDispatchToProps = {
  fetchPage: PagesActions.fetchPage,
  fetchPages: PagesActions.fetchPages,
  saveCache: PagesActions.saveCache,
  clearCache: PagesActions.clearCache
};
const connector = connect(mapStateToProps, mapDispatchToProps);
class PageSelect extends Component {
  constructor(props) {
    super(props);
    this.onChange = this.onChange.bind(this);
    this.loadOptions = this.loadOptions.bind(this);
    this.renderOption = this.renderOption.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchPage,
      value
    } = this.props;
    if (value) {
      fetchPage(value).catch(err => {
        throw err;
      });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      fetchPage,
      pages,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    if (!isEqual(prevValue, value) && !pages[value]) {
      fetchPage(value).catch(err => {
        throw err;
      });
    }
  }
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      this.props.clearCache();
      offset = 0;
    }
    const query = {
      name: input,
      limit: DEFAULT_PAGE_SIZE,
      offset
    };
    this.props.fetchPages(query).then(response => {
      const {
        total
      } = response;
      callback(null, {
        options: this.props.options,
        pagination: {
          hasMore: total > this.props.options.length,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  onChange(evt) {
    const {
      onChange,
      onFieldChange = emptyFunction
    } = this.props;
    if (onChange) {
      onChange(evt);
    } else {
      onFieldChange(evt.target.value);
    }
  }
  renderOption(option) {
    const {
      disabled,
      dropdownText,
      text,
      help
    } = option;
    const renderedOption = /*#__PURE__*/_jsxs("div", {
      title: text,
      className: "m-x-4 p-y-2",
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(UITruncateString, {
          tooltip: !disabled,
          children: text || dropdownText
        })
      }), /*#__PURE__*/_jsx(UITruncateString, {
        tooltip: !disabled,
        children: /*#__PURE__*/_jsx(Small, {
          children: help
        })
      })]
    });
    if (option.disabled) {
      return /*#__PURE__*/_jsx(UITooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ContentComponents.cmv2.fields.page.disabledTooltip"
        }),
        children: renderedOption
      });
    }
    return renderedOption;
  }
  render() {
    const _this$props = this.props,
      {
        readOnly,
        required = false,
        extraOptions = [],
        defaultOptions = [],
        type = PAGE
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const newDefaultOptions = defaultOptions.concat(extraOptions);
    return /*#__PURE__*/_jsx(AsyncSelector, Object.assign({
      "data-selenium": "page-select",
      clearable: !required,
      defaultOptions: newDefaultOptions,
      loadOptions: this.debouncedLoadOptions,
      onChange: this.onChange,
      optionRenderer: this.renderOption,
      optionRendererIgnoresSpecialOptions: true,
      readOnly: readOnly,
      type: type
    }, rest));
  }
}
export default connector(PageSelect);